export const GLAZING_OPTIONS = [
  "none",
  "standard",
  "anti_reflective",
  "optium_museum",
] as const;

export type GlazingOption = typeof GLAZING_OPTIONS[number];

export const DEFAULT_GLAZING: GlazingOption = "standard";

type GlazingLocale = "en" | "fr";

const getGlazingLocale = (language?: string): GlazingLocale =>
  language && language.toLowerCase().startsWith("en") ? "en" : "fr";

const GLAZING_TYPE_LABELS: Record<GlazingLocale, string> = {
  en: "Glazing",
  fr: "Verre",
};

const GLAZING_LABELS: Record<GlazingOption, Record<GlazingLocale, string>> = {
  none: {
    en: "No glass",
    fr: "Sans verre",
  },
  standard: {
    en: "Regular acrylic",
    fr: "Standard",
  },
  anti_reflective: {
    en: "Anti-reflective acrylic",
    fr: "Antireflet",
  },
  optium_museum: {
    en: "Optium Museum Acrylic",
    fr: "Optium Museum",
  },
};

const GLAZING_DESCRIPTIONS: Record<
  GlazingOption,
  Record<GlazingLocale, string>
> = {
  none: {
    en: "Skip the acrylic front for an open presentation. This option is useful when you want a lighter build or when glazing is not desired for the final look.",
    fr: "Cette option retire la vitre acrylique de face pour une presentation ouverte. Elle convient si vous souhaitez un rendu plus leger ou si vous ne voulez pas de vitrage sur le cadre final.",
  },
  standard: {
    en: "Our standard acrylic offers everyday protection with a clean finish and UV filtering, making it the default choice for most framed artworks.",
    fr: "Notre verre acrylique standard offre une protection du quotidien avec un rendu net et un filtre UV, ce qui en fait le choix par defaut pour la plupart des encadrements.",
  },
  anti_reflective: {
    en: "This acrylic reduces distracting reflections while keeping your artwork protected, making it a great choice for bright rooms and wall displays opposite windows.",
    fr: "Ce verre acrylique limite les reflets parasites tout en protegeant votre oeuvre. Il est particulierement adapte aux pieces lumineuses et aux murs exposes aux fenetres.",
  },
  optium_museum: {
    en: "Our museum-grade acrylic delivers the clearest presentation, strong UV protection and premium anti-reflective performance for the most demanding pieces.",
    fr: "Notre acrylique de qualite musee offre la presentation la plus limpide, une forte protection UV et des performances antireflet premium pour les oeuvres les plus exigeantes.",
  },
};

export const GLAZING_PRICE_FIELDS: Partial<Record<GlazingOption, string>> = {
  standard: "glazing_standard",
  anti_reflective: "glazing_anti_reflective",
  optium_museum: "glazing_optium_museum",
};

const LEGACY_GLAZING_MAP: { [key: string]: GlazingOption } = {
  none: "none",
  "no glass": "none",
  no_glass: "none",
  sans_verre: "none",
  "sans verre": "none",
  standard: "standard",
  "regular acrylic": "standard",
  regular_acrylic: "standard",
  antireflet: "anti_reflective",
  anti_reflective: "anti_reflective",
  "anti reflective": "anti_reflective",
  "anti-reflective": "anti_reflective",
  "anti-reflective acrylic": "anti_reflective",
  optium_museum: "optium_museum",
  "optium museum": "optium_museum",
  "optium museum acrylic": "optium_museum",
};

export const normalizeGlazingSelection = (
  value?: string | null
): GlazingOption | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }

  const normalizedValue = value.trim().toLowerCase();
  return LEGACY_GLAZING_MAP[normalizedValue];
};

export const resolveGlazingSelection = (
  glazingSelected?: string | null,
  legacyGlazingSelected?: string | null
): GlazingOption | undefined =>
  normalizeGlazingSelection(glazingSelected) ||
  normalizeGlazingSelection(legacyGlazingSelected);

export const isCanvasArtType = (artType?: string | null): boolean =>
  typeof artType === "string" && artType.toLowerCase().includes("canvas");

export const isCanvasConfiguration = (
  artType?: string | null,
  mount?: string | null
): boolean => mount === "canvas" || isCanvasArtType(artType);

export const isGlazingEligible = (
  artType?: string | null,
  mount?: string | null
): boolean => !isCanvasConfiguration(artType, mount);

export const getGlazingPayloadValue = (
  glazingSelected?: string | null,
  artType?: string | null,
  mount?: string | null
): GlazingOption | undefined => {
  if (!isGlazingEligible(artType, mount)) {
    return undefined;
  }

  return resolveGlazingSelection(glazingSelected) || DEFAULT_GLAZING;
};

export const getGlazingPriceField = (
  glazingSelected?: string | null
): string | undefined => {
  const resolvedGlazing = resolveGlazingSelection(glazingSelected);
  if (!resolvedGlazing) {
    return undefined;
  }

  return GLAZING_PRICE_FIELDS[resolvedGlazing];
};

export const isGlazingPriceEnabled = (
  priceRange: any,
  glazing: GlazingOption
): boolean => {
  const priceField = getGlazingPriceField(glazing);

  if (!priceField) {
    return true;
  }

  if (!priceRange) {
    return false;
  }

  const enabledValue = priceRange[`${priceField}_enabled`];

  if (enabledValue === false) {
    return false;
  }

  if (enabledValue === true) {
    return true;
  }

  return Number(priceRange[priceField] || 0) > 0;
};

export const hasGlazingAvailabilityData = (priceRange: any): boolean =>
  !!priceRange &&
  Object.values(GLAZING_PRICE_FIELDS).some(
    (priceField) =>
      !!priceField &&
      (`${priceField}_enabled` in priceRange || priceField in priceRange)
  );

export const getAvailableGlazingOptions = (priceRange: any): GlazingOption[] => {
  if (!hasGlazingAvailabilityData(priceRange)) {
    return [];
  }

  return GLAZING_OPTIONS.filter((glazing) =>
    isGlazingPriceEnabled(priceRange, glazing)
  );
};

export const getFallbackGlazingOption = (priceRange: any): GlazingOption =>
  getAvailableGlazingOptions(priceRange).find((glazing) => glazing !== "none") ||
  "none";

export const getGlazingTypeLabel = (language?: string): string =>
  GLAZING_TYPE_LABELS[getGlazingLocale(language)];

export const getGlazingLabel = (
  glazing: GlazingOption,
  language?: string
): string => GLAZING_LABELS[glazing][getGlazingLocale(language)];

export const getGlazingDescription = (
  glazing: GlazingOption,
  language?: string
): string => GLAZING_DESCRIPTIONS[glazing][getGlazingLocale(language)];
